<template>
	<BaseModal
		v-model:visible="visible"
		@cancel="close"
		@ok="save"
		:show-cancel-button="true"
		:show-ok-button="true"
		:ok-label="t('common.add')"
		width="350px"
		:use-custom-header-footer="true"
	>
		<template #header>
			<div class="flex flex-col">
				<div class="text-sm font-light opacity-85">{{ t('queue.addExternalToQueueHeader') }}</div>
				<div class="text-2xl opacity-85" style="margin-top:-5px">{{ queue.name }}</div>
			</div>
		</template>
		<template #content>
			<div class="flex mt-2 gap-2">
				<div class="w-3/4">
					<div>{{ t('queue.externalPhoneNumber') }}</div>
					<InputText :state="numberIsValid" v-model="number" size="small" fluid maxlength="20" :autofocus="true" tabindex="100" @keydown="keyDownHandler" />
				</div>
				<div class="w-1/4">
					<div>{{ t('queue.priority') }}</div>
					<div class="w-1/4">
						<Select v-model="priority" :options="priorities" size="small" option-value="value" option-label="label" />
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<Button severity="secondary" size="small" @click="close()" :disabled="isSaving" tabindex="110" :label="t('common.cancel')" class="w-36" />
			<Button severity="primary" size="small" :disabled="!numberIsValid || isSaving" @click="save()" tabindex="120" :label="t('common.add')" class="w-36" />
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import globalEvents from '@/utilities/GlobalEvents'
import { useSettingsStore } from '@/stores/Settings'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import type { PbxAppQueuesAllItem } from '@/TcxApi/PbxApp'
import { computed, ref } from 'vue'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import { isPhoneNumber } from '@/utilities/Tools'
import BaseModal from './BaseModal.vue'

const ss = useSettingsStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	queue: PbxAppQueuesAllItem
}

const props = defineProps<Props>()
const number = ref('')
const priority = ref(1)
const isSaving = ref(false)
const priorities = ref<{ value: number, label: string }[]>([])
const visible = ref(false)

const numberIsValid = computed(() => {
	return isPhoneNumber(number.value, false)
})

function keyDownHandler(event: KeyboardEvent) {
	if (event.key === 'Enter') {
		event.preventDefault()
		save()
	}
}

function show() {
	number.value = ''
	priority.value = 1
	visible.value = true
}

function close() {
	visible.value = false
}

async function save() {
	if (!numberIsValid.value) { return }
	let sNumber = number.value

	if (sNumber.startsWith('00')) { sNumber = '+' + sNumber.substring(2) }
	if (!sNumber.startsWith('+')) { sNumber = ss.pbxSettings.nationalPrefix + sNumber }
	isSaving.value = true

	try {
		await api.post(`/pbx/queue/${props.queue._id}/member`, { number: sNumber, priority: priority.value })
		isSaving.value = false
		close()
	}
	catch (err) {
		isSaving.value = false
		if ((err as ErrorResponseDetail).status === 0) {
			closeAndShowError(t('common.error.commErrorMessage'), t('common.error.commErrorHeader'))
		} else if ((err as ErrorResponseDetail).status === 409 && (err as ErrorResponseDetail).code === 'member') {
			close() // Already a member
		} else if ((err as ErrorResponseDetail).status === 403 && (err as ErrorResponseDetail).code === 'dynamic') {
			closeAndShowError(t('queue.queueDoesNotAllowDynamicMembers'))
		} else if ((err as ErrorResponseDetail).status === 409 && (err as ErrorResponseDetail).code === 'extension') {
			closeAndShowError(t('queue.queueOnlyAcceptExtensions'))
		} else {
			closeAndShowError(`${t('common.genericErrorHeader')}: ${(err as ErrorResponseDetail).status} ${(err as ErrorResponseDetail).path}\n${(err as ErrorResponseDetail).message}`)
		}
	}
}

function showError(message: string, header?: string) {
	globalEvents.emit('CommonErrorModal', { header: header ?? t('queue.addMemberToQueueErrorHeader'), message: message })
}

function closeAndShowError(message: string, header?: string) {
	close()
	showError(message, header)
}

globalEvents.on('Auth:LoggedOut', close)
globalEvents.on('Realtime:Disconnected', close)

for (let index = 1; index <= 10; index++) {
	priorities.value.push({ value: index, label: `${index}` })
}

defineExpose({ show })
</script>

<style scoped>
	.employee-picture {
		position: absolute;
		top: 0px;
		left: 0px;
	}
</style>
